<template>
  <v-container fluid class="clinicInformation px-12 py-4">
    <v-row>
      <v-col cols="12">
        <breadcrumbs></breadcrumbs>
        <h1>Patient Statements</h1>
        <div class="caption font-italic">
          (Sample statement available at bottom of worksheet)
        </div>
        <v-divider class="my-3"></v-divider>
      </v-col>
    </v-row>

    <v-row>
      <v-col :cols="12">
        <h2 class="title mb-3">System Startup</h2>
        <v-card outlined class="mt-2">
          <v-card-text>
            <table>
              <tr>
                <th valign="top">Minimum statement balance:</th>
                <td>
                  {{
                    organization.billingPatientStatementMinimumBalance
                      ? `${organization.billingPatientStatementMinimumBalance}`
                      : "--"
                  }}
                </td>
              </tr>
              <tr>
                <th valign="top">Small balance write-off?</th>
                <td>
                  {{
                    organization.billingPatientStatementWriteOffSmallBalanceYN
                      ? "Yes"
                      : "No"
                  }}
                </td>
              </tr>
              <tr
                v-if="
                  organization.billingPatientStatementWriteOffSmallBalanceYN
                "
              >
                <th valign="top">Small balance write-off amount:</th>
                <td>
                  {{
                    organization.billingPatientStatementWriteOffAmount
                      ? `$${organization.billingPatientStatementWriteOffAmount}`
                      : "--"
                  }}
                </td>
              </tr>
              <tr>
                <th valign="top">Statement Message:</th>
                <td>
                  {{ organization.billingPatientStatementMessage || "--" }}
                </td>
              </tr>
            </table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-divider class="mt-6"></v-divider>

    <v-row class="mt-6">
      <v-col :cols="12">
        <h2 class="title mb-3">Practice Return Information</h2>
        <v-card outlined class="mt-2">
          <v-card-text>
            <table>
              <tr>
                <th valign="top">Practice return name:</th>
                <td>
                  {{
                    organization.billingPatientStatementPracticeReturnName ||
                    "--"
                  }}
                </td>
              </tr>
              <tr>
                <th valign="top">Practice return address:</th>
                <td>
                  <span v-html="returnAddress"> </span>
                </td>
              </tr>
            </table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-divider class="mt-6"></v-divider>

    <v-row class="mt-6">
      <v-col :cols="12">
        <h2 class="title mb-3">Practice Remit-To Information</h2>
        <v-card outlined class="mt-2">
          <v-card-text>
            <table>
              <tr>
                <th valign="top">Practice remit-to name:</th>
                <td>
                  {{
                    organization.billingPatientStatementPracticeRemitToInfoName ||
                    "--"
                  }}
                </td>
              </tr>
              <tr>
                <th valign="top">Practice remit-to address:</th>
                <td>
                  <span v-html="remitToAddress"> </span>
                </td>
              </tr>
            </table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-divider class="mt-6"></v-divider>

    <v-row class="mt-6">
      <v-col :cols="12">
        <h2 class="title mb-3">Additional Printed Information</h2>
        <v-card outlined class="mt-2">
          <v-card-text>
            <table>
              <tr>
                <th valign="top">Billing phone number:</th>
                <td>
                  {{ organization.billingPatientStatementPhoneNumber || "--" }}
                </td>
              </tr>
              <tr>
                <th valign="top">Credit cards accepted?</th>
                <td>
                  {{
                    organization.billingPatientStatementCreditCardsYN
                      ? "Yes"
                      : "No"
                  }}
                </td>
              </tr>
              <tr>
                <th valign="top">Accepted credit cards:</th>
                <td>
                  {{ acceptedCreditCards }}
                </td>
              </tr>
            </table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-divider class="mt-6"></v-divider>

    <v-row class="mt-6">
      <v-col :cols="12">
        <h2 class="title mb-3">Optional Information</h2>
        <v-card outlined class="mt-2">
          <v-card-text>
            <table>
              <tr>
                <th valign="top">Office phone number:</th>
                <td>
                  {{
                    organization.billingPatientStatementOfficePhoneNumber ||
                    "--"
                  }}
                </td>
              </tr>
              <tr>
                <th valign="top">Office hours:</th>
                <td>
                  {{ organization.billingPatientStatementOfficeHours || "--" }}
                </td>
              </tr>
              <tr>
                <th valign="top">Email address:</th>
                <td>
                  {{ organization.billingPatientStatementEmail || "--" }}
                </td>
              </tr>
              <tr>
                <th valign="top">Practice website:</th>
                <td>
                  {{ organization.billingPatientStatementWebsite || "--" }}
                </td>
              </tr>
              <tr>
                <th valign="top">Fax number:</th>
                <td>
                  {{ organization.billingPatientStatementFaxNumber || "--" }}
                </td>
              </tr>
              <tr>
                <th valign="top">Practice logo?</th>
                <td>
                  {{
                    organization.billingPatientStatementPracticeLogoYN
                      ? "Yes"
                      : "No"
                  }}
                </td>
              </tr>
              <tr>
                <th valign="top">Logo:</th>
                <td>
                  <img
                    v-if="organization.billingPatientStatementPracticeLogo"
                    :src="`/api/file2/${organization.billingPatientStatementPracticeLogo}/download`"
                  />
                  <span v-if="!organization.billingPatientStatementPracticeLogo"
                    >--</span
                  >
                </td>
              </tr>
            </table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment";
import Breadcrumbs from "@/views/Breadcrumbs";

export default {
  components: {
    Breadcrumbs,
  },
  computed: {
    ...mapGetters({
      getAddress: "address/getById",
    }),
    ...mapState({
      addressRecord: state => state.address.record,
      locationList: state => state.location.list,
      organizationRecord: state => state.organization.record,
      organizationId: state => state.session.organizationId,
    }),
    acceptedCreditCards() {
      const creditCards = this.organization
        .billingPatientStatementCreditCardsAccepted;
      if (creditCards) {
        return creditCards.join(", ");
      }
      return "--";
    },
    locationCount() {
      if (this.locationList) {
        return this.locationList.length;
      }
      return "--";
    },
    payToAddress() {
      if (!this.organizationRecord) return "--";
      return this.formatAddress(
        this.organizationRecord.billingClaimsPayToAddress,
        true
      );
    },
    remitToAddress() {
      if (!this.organizationRecord) return "--";
      return this.formatAddress(
        this.organizationRecord
          .billingPatientStatementPracticeRemitToInfoAddress,
        true
      );
    },
    returnAddress() {
      if (!this.organizationRecord) return "--";
      return this.formatAddress(
        this.organizationRecord.billingPatientStatementPracticeReturnAddress,
        true
      );
    },
  },
  async created() {
    await this.getOrganization(this.organizationId);
    this.organization = cloneDeep(this.organizationRecord);
    await this.getAddressList();
    await this.getLocationList();
  },
  data() {
    return {
      addressList: [],
      organization: {},
    };
  },
  methods: {
    ...mapActions({
      getAddressList: "address/list",
      getLocationList: "location/list",
      getOrganization: "organization/getByOrganizationId",
    }),
    formatAddress(addressId, blockFormat) {
      const addressRecord = this.getAddress(addressId);
      if (!addressRecord) return "--";
      const { address, address2, city, state, zip } = addressRecord;

      if (blockFormat) {
        return `${address}${
          address2 ? "<br>" + address2 : ""
        }<br>${city}, ${state} ${zip}`;
      }
      return `${address}, ${
        address2 ? address2 + ", " : ""
      }${city}, ${state} ${zip}`;
    },
    formatDate(date) {
      return moment.utc(date).format("MMMM DD, YYYY");
    },
    isEnabled(slot) {
      return this.enabled === slot;
    },
  },
};
</script>

<style>
.clinicInformation th {
  text-align: right !important;
  min-width: 250px;
}
.clinicInformation td {
  padding-left: 15px !important;
}
</style>
